if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          navigationButtons: this.querySelectorAll('[data-gallery-direction]'),
          mobileIndicators: this.querySelector('[data-ltp-mobile-gallery-indicators]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (this.dataset.ltpMobileLifestyleFirst === 'true') {
          this.configureMobileLifestyle(true);
          this.mql.addEventListener('change', () => this.configureMobileLifestyle(false));
        }
        if (this.dataset.ltpEditorialGallery === 'true') {
          const opening = this.dataset.ltpMobileLifestyleFirst === 'true' && !this.mql.matches
            ? this.querySelector('[data-ltp-mobile-lifestyle] img')
            : document.querySelector('[data-ltp-editorial-intro] img');
          this.ltpOpeningReady = new Promise((resolve) => {
            const ready = () => {
              this.ltpOpeningSettled = true;
              resolve();
            };
            this.releaseEditorialOpening = ready;
            if (!opening || opening.complete) ready();
            else {
              opening.addEventListener('load', ready, { once: true });
              opening.addEventListener('error', ready, { once: true });
            }
          });
          this.ltpInitialPositionPending = true;
          requestAnimationFrame(() => {
            if (this.ltpMobileLoop) {
              this.ltpInitialPositionPending = false;
              return;
            }
            const active = this.elements.viewer.querySelector('[data-media-id].is-active');
            const slider = this.elements.viewer.slider;
            if (active && slider) slider.scrollTo({ left: this.getGalleryScrollTarget(active, slider), behavior: 'instant' });
            this.elements.viewer.enableSliderLooping = true;
            this.elements.viewer.resetPages?.();
            requestAnimationFrame(() => { this.ltpInitialPositionPending = false; });
          });
        }
        this.setupDeferredImageLoading();
        this.elements.viewer.slider?.addEventListener(
          'pointerdown',
          () => {
            this.cancelGalleryAnimation();
            if (this.dataset.ltpCanonicalGallery === 'true') {
              this.navigationToken = (this.navigationToken || 0) + 1;
            }
            if (!this.ltpMobileLoop) this.prepareNavigationTarget('next', 'low');
          },
          { passive: true }
        );
        this.elements.mobileIndicators?.addEventListener('click', (event) => {
          const indicator = event.target.closest('[data-ltp-gallery-indicator]');
          if (!indicator || indicator.hidden || !this.ltpMobileLoop) return;
          this.navigateMobilePosition(indicator.dataset.ltpGalleryIndicator);
        });
        if (this.elements.mobileIndicators) {
          this.setMobileIndicatorCount(this.dataset.ltpSlideCount === '2');
        }
        if (!this.elements.thumbnails) return;

        const slideChangedHandler =
          this.dataset.ltpCanonicalGallery === 'true'
            ? this.onSlideChanged.bind(this)
            : debounce(this.onSlideChanged.bind(this), 500);
        this.elements.viewer.addEventListener('slideChanged', slideChangedHandler);
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.elements.navigationButtons.forEach((button) => {
          button.addEventListener('click', this.navigateMedia.bind(this, button.dataset.galleryDirection));
          button.addEventListener(
            'pointerenter',
            () => this.prepareNavigationTarget(button.dataset.galleryDirection, 'low'),
            { passive: true }
          );
          button.addEventListener('focus', () =>
            this.prepareNavigationTarget(button.dataset.galleryDirection, 'low')
          );
          button.addEventListener('pointerdown', () =>
            this.prepareNavigationTarget(button.dataset.galleryDirection, 'high'), { passive: true }
          );
        });
        this.updateNavigationState();
        this.scheduleCanonicalImages();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      setupDeferredImageLoading() {
        if (this.dataset.ltpEditorialGallery === 'true') return;
        const deferredImages = this.querySelectorAll('img[data-ltp-deferred-image][data-ltp-deferred-src]');
        if (!deferredImages.length) return;

        if (!('IntersectionObserver' in window)) return;

        this.deferredImageObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              this.loadDeferredImage(entry.target);
              this.deferredImageObserver.unobserve(entry.target);
            });
          },
          {
            root: this.elements.viewer.slider || this.elements.viewer,
            rootMargin: '0px 50%',
          }
        );

        deferredImages.forEach((image) => this.deferredImageObserver.observe(image));
      }

      configureMobileLifestyle(initial) {
        const mobile = !this.mql.matches;
        const slider = this.elements.viewer.slider;
        const lifestyle = slider?.querySelector('[data-ltp-gallery-position="4"]');
        const thumbnail = this.elements.thumbnails?.querySelector('[data-ltp-gallery-position="4"]');
        if (!slider || !lifestyle || !thumbnail) return;
        this.mobileLoopEvents?.abort();
        this.mobileLoopResize?.disconnect();
        cancelAnimationFrame(this.mobileSettleFrame);
        slider.querySelectorAll('[data-ltp-loop-position]').forEach((clone) => clone.remove());
        this.ltpMobileLoop = mobile;
        if (mobile) {
          slider.prepend(lifestyle);
          thumbnail.parentElement.prepend(thumbnail);
        } else {
          slider.append(lifestyle);
          thumbnail.parentElement.append(thumbnail);
        }
        if (initial && !mobile) {
          const desktopPosition = this.dataset.ltpDesktopInitialPosition;
          slider.querySelectorAll('[data-ltp-gallery-position]').forEach((slide) => {
            slide.classList.toggle('is-active', slide.dataset.ltpGalleryPosition === desktopPosition);
          });
        }
        if (initial && mobile && this.dataset.ltpMobileOpening === 'lifestyle') {
          slider.querySelectorAll('[data-media-id]').forEach((slide) => slide.classList.toggle('is-active', slide === lifestyle));
          slider.removeAttribute('aria-hidden');
          const hero = this.querySelector('[data-ltp-selected-variant-hero]');
          if (hero) {
            hero.hidden = true;
            hero.setAttribute('aria-hidden', 'true');
          }
        }
        const sectionId = this.closest('product-info')?.dataset.section;
        const modalImage = document.querySelector(`#ProductModal-${sectionId} [data-ltp-modal-image]:nth-of-type(4)`);
        const source = lifestyle.querySelector('[data-ltp-mobile-lifestyle] source');
        if (modalImage && source) {
          this.ltpDesktopLifestyleModal ||= {
            src: modalImage.dataset.ltpDeferredSrc,
            srcset: modalImage.dataset.ltpDeferredSrcset,
          };
          modalImage.removeAttribute('src');
          modalImage.removeAttribute('srcset');
          modalImage.dataset.ltpDeferredSrc = mobile ? source.srcset.split(',')[0].trim().split(' ')[0] : this.ltpDesktopLifestyleModal.src;
          modalImage.dataset.ltpDeferredSrcset = mobile ? source.srcset : this.ltpDesktopLifestyleModal.srcset;
        }
        this.elements.viewer.resetPages?.();
        if (mobile) this.initialiseMobileLoop();
        if (!initial) {
          const active = slider.querySelector('[data-media-id].is-active');
          if (active && !mobile) slider.scrollTo({ left: this.getGalleryScrollTarget(active, slider), behavior: 'instant' });
          this.updateNavigationState();
        }
      }

      initialiseMobileLoop() {
        const slider = this.elements.viewer.slider;
        this.mobileLoopEvents = new AbortController();
        const options = { passive: true, signal: this.mobileLoopEvents.signal };
        this.mobileTouching = false;
        this.refreshMobileLoop();
        slider.addEventListener('touchstart', () => {
          this.mobileTouching = true;
          cancelAnimationFrame(this.mobileSettleFrame);
        }, options);
        const release = () => {
          this.mobileTouching = false;
          this.watchMobileSettle();
        };
        slider.addEventListener('touchend', release, options);
        slider.addEventListener('touchcancel', release, options);
        slider.addEventListener('scroll', () => this.watchMobileSettle(), options);
        slider.addEventListener('scrollend', () => this.watchMobileSettle(), options);
        this.mobileLoopResize = new ResizeObserver(() => {
          if (this.mobileTouching) return;
          const width = slider.clientWidth;
          if (width === this.mobileLoopWidth) return;
          this.mobileLoopWidth = width;
          this.refreshMobileLoop();
        });
        this.mobileLoopResize.observe(slider);
      }

      refreshMobileLoop() {
        if (!this.ltpMobileLoop) return;
        const slider = this.elements.viewer.slider;
        const active = slider.querySelector('[data-ltp-gallery-position].is-active');
        this.mobileRebasing = true;
        slider.querySelectorAll('[data-ltp-loop-position]').forEach((clone) => clone.remove());
        const slides = [...slider.children].filter((slide) => slide.dataset.ltpGalleryPosition && !slide.hidden);
        const cloneSlide = (slide) => {
          const clone = slide.cloneNode(true);
          clone.dataset.ltpLoopPosition = slide.dataset.ltpGalleryPosition;
          clone.removeAttribute('data-ltp-gallery-position');
          clone.classList.remove('is-active');
          clone.setAttribute('aria-hidden', 'true');
          clone.inert = true;
          [clone, ...clone.querySelectorAll('[id], [data-media-id]')].forEach((element) => {
            element.removeAttribute('id');
            element.removeAttribute('data-media-id');
          });
          return clone;
        };
        if (slides.length > 1) {
          slider.prepend(cloneSlide(slides[slides.length - 1]));
          slider.append(cloneSlide(slides[0]));
        }
        if (active) this.rebaseMobileSlide(active);
        this.updateMobileIndicators();
        this.mobileRebasing = false;
      }

      rebaseMobileSlide(slide) {
        const slider = this.elements.viewer.slider;
        const ownsRebase = !this.mobileRebasing;
        if (ownsRebase) this.mobileRebasing = true;
        slider.style.scrollSnapType = 'none';
        slider.scrollTo({ left: this.getGalleryScrollTarget(slide, slider), behavior: 'instant' });
        slider.getBoundingClientRect();
        slider.style.removeProperty('scroll-snap-type');
        if (ownsRebase) {
          requestAnimationFrame(() => { this.mobileRebasing = false; });
        }
      }

      watchMobileSettle() {
        cancelAnimationFrame(this.mobileSettleFrame);
        if (this.mobileTouching || this.mobileRebasing || !this.ltpMobileLoop) return;
        const slider = this.elements.viewer.slider;
        let last = slider.scrollLeft;
        let stable = 0;
        const check = () => {
          if (this.mobileTouching || this.mobileRebasing || !this.ltpMobileLoop) return;
          const current = slider.scrollLeft;
          stable = Math.abs(current - last) < .1 ? stable + 1 : 0;
          last = current;
          if (stable >= 3) {
            this.commitMobileSlide();
            return;
          }
          this.mobileSettleFrame = requestAnimationFrame(check);
        };
        this.mobileSettleFrame = requestAnimationFrame(check);
      }

      commitMobileSlide() {
        if (!this.ltpMobileLoop || this.mobileTouching || this.mobileRebasing) return;
        const slider = this.elements.viewer.slider;
        const slides = [...slider.children].filter((slide) => !slide.hidden);
        const target = slides.reduce((best, slide) => {
          const distance = Math.abs(this.getGalleryScrollTarget(slide, slider) - slider.scrollLeft);
          return !best || distance < best.distance ? { slide, distance } : best;
        }, null);
        if (!target || target.distance > 2) return;
        const position = target.slide.dataset.ltpLoopPosition || target.slide.dataset.ltpGalleryPosition;
        const original = slider.querySelector(`[data-ltp-gallery-position="${position}"]`);
        if (!original) return;
        if (target.slide !== original) this.rebaseMobileSlide(original);
        if (original.classList.contains('is-active')) return;
        this.selectMobileSlide(original, 'swipe');
      }

      selectMobileSlide(slide, source) {
        const slider = this.elements.viewer.slider;
        slider.querySelectorAll('[data-ltp-gallery-position]').forEach((item) => item.classList.toggle('is-active', item === slide));
        this.dispatchCanonicalPositionChange(slide, source);
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${slide.dataset.mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
        this.updateNavigationState();
        this.updateMobileIndicators();
      }

      setMobileIndicatorCount(printOnly) {
        const indicators = this.elements.mobileIndicators;
        if (!indicators) return;
        indicators.dataset.ltpIndicatorCount = printOnly ? '2' : '4';
        indicators.querySelectorAll('[data-ltp-gallery-indicator="2"], [data-ltp-gallery-indicator="3"]')
          .forEach((indicator) => { indicator.hidden = printOnly; });
        this.updateMobileIndicators();
      }

      updateMobileIndicators() {
        const indicators = this.elements.mobileIndicators;
        if (!indicators) return;
        const activePosition = this.elements.viewer.slider
          ?.querySelector('[data-ltp-gallery-position].is-active')?.dataset.ltpGalleryPosition;
        indicators.querySelectorAll('[data-ltp-gallery-indicator]').forEach((indicator) => {
          if (indicator.hidden) {
            indicator.removeAttribute('aria-current');
            return;
          }
          if (indicator.dataset.ltpGalleryIndicator === activePosition) {
            indicator.setAttribute('aria-current', 'true');
          } else {
            indicator.removeAttribute('aria-current');
          }
        });
      }

      navigateMobilePosition(position) {
        if (!this.ltpMobileLoop) return;
        const slider = this.elements.viewer.slider;
        const candidates = [...slider.children].filter((slide) =>
          !slide.hidden &&
          (slide.dataset.ltpGalleryPosition === position || slide.dataset.ltpLoopPosition === position)
        );
        if (!candidates.length) return;
        const target = candidates.reduce((nearest, slide) => {
          const distance = Math.abs(this.getGalleryScrollTarget(slide, slider) - slider.scrollLeft);
          return !nearest || distance < nearest.distance ? { slide, distance } : nearest;
        }, null)?.slide;
        if (!target) return;
        slider.scrollTo({
          left: this.getGalleryScrollTarget(target, slider),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
        });
        this.watchMobileSettle();
      }

      getDeferredImage(imageOrContainer) {
        if (this.dataset.ltpCanonicalGallery === 'true') {
          return imageOrContainer?.matches?.('img')
            ? imageOrContainer
            : imageOrContainer?.querySelector?.('img');
        }
        return imageOrContainer?.matches?.('img[data-ltp-deferred-image]')
          ? imageOrContainer
          : imageOrContainer?.querySelector?.('img[data-ltp-deferred-image]');
      }

      loadDeferredImage(imageOrContainer, fetchPriority = 'auto') {
        const image = this.getDeferredImage(imageOrContainer);
        if (!image) return image;

        if (this.dataset.ltpCanonicalGallery !== 'true' || image.fetchPriority !== 'high') {
          image.fetchPriority = fetchPriority;
        }
        if (!image.dataset.ltpDeferredSrc) return image;
        if (image.dataset.ltpDeferredSrcset) image.srcset = image.dataset.ltpDeferredSrcset;
        image.src = image.dataset.ltpDeferredSrc;
        delete image.dataset.ltpDeferredSrcset;
        delete image.dataset.ltpDeferredSrc;
        this.deferredImageObserver?.unobserve(image);
        return image;
      }

      prepareMediaImage(imageOrContainer, fetchPriority = 'auto') {
        const image = this.getDeferredImage(imageOrContainer);
        if (!image) return Promise.resolve(null);

        image.loading = 'eager';
        this.loadDeferredImage(image, fetchPriority);
        if (image.ltpDecoded && image.ltpReadySource !== image.currentSrc) {
          image.ltpDecoded = false;
          image.ltpReadyPromise = null;
        }
        if (!image.ltpReadyPromise) {
          if (image.decode) {
            image.ltpReadyPromise = image.decode().catch(() => undefined).then(() => image);
          } else if (image.complete) {
            image.ltpReadyPromise = Promise.resolve(image);
          } else {
            image.ltpReadyPromise = new Promise((resolve) => {
              const finish = () => resolve(image);
              image.addEventListener('load', finish, { once: true });
              image.addEventListener('error', finish, { once: true });
            });
          }
          image.ltpReadyPromise = image.ltpReadyPromise.then(() => {
            image.ltpDecoded = Boolean(image.naturalWidth);
            image.ltpReadySource = image.currentSrc;
            return image;
          });
        }
        return image.ltpReadyPromise;
      }

      scheduleCanonicalImages() {
        if (this.dataset.ltpCanonicalGallery !== 'true') return;
        if (this.ltpMobileLoop) return this.ltpSelectedImageReady;
        if (this.dataset.ltpEditorialGallery === 'true' && !this.ltpOpeningSettled) {
          if (!this.ltpOpeningScheduled) {
            this.ltpOpeningScheduled = true;
            this.ltpOpeningReady.then(() => this.scheduleCanonicalImages());
          }
          return this.ltpOpeningReady;
        }
        const media = Array.from(this.elements.viewer.querySelectorAll('[data-media-id][data-ltp-gallery-position]'))
          .filter((item) => window.getComputedStyle(item).display !== 'none');
        const contextual = this.dataset.ltpContextualGallery === 'true';
        const active = contextual ? media.find((item) => item.classList.contains('is-active')) || media[0] : media[0];
        if (!active) return;
        const selectedReady = this.ltpSelectedImageReady;
        const ready = Promise.all([selectedReady, this.prepareMediaImage(active, 'high')]);
        const warmToken = (this.canonicalWarmToken || 0) + 1;
        this.canonicalWarmToken = warmToken;
        this.canonicalWarmPromise = ready.then(() => new Promise((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        })).then(async () => {
          if (warmToken !== this.canonicalWarmToken || selectedReady !== this.ltpSelectedImageReady) return;
          const siblings = contextual
            ? media.filter((item) => item !== active && item.dataset.ltpGalleryPosition !== '4')
            : media.slice(1, 3);
          await Promise.all(siblings.map((item) => this.prepareMediaImage(item, 'low')));
          if (warmToken !== this.canonicalWarmToken || selectedReady !== this.ltpSelectedImageReady) return;
          const lifestyle = media.find((item) => item.dataset.ltpGalleryPosition === '4');
          if (lifestyle && lifestyle !== active) await this.prepareMediaImage(lifestyle, 'low');
        });
        return this.canonicalWarmPromise;
      }

      onSlideChanged(event) {
        if (this.ltpMobileLoop) return;
        if (this.ltpInitialPositionPending) return;
        if (this.dataset.ltpCanonicalGallery === 'true') {
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((media) => {
            media.classList.toggle('is-active', media === event.detail.currentElement);
          });
          this.dispatchCanonicalPositionChange(event.detail.currentElement, 'swipe');
          this.scheduleCanonicalImages();
        }
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.updateNavigationState();
      }

      getAvailableMediaTargets() {
        return Array.from(this.elements.thumbnails?.querySelectorAll('[data-target]') || []).filter(
          (thumbnail) => window.getComputedStyle(thumbnail).display !== 'none'
        );
      }

      getNavigationTarget(direction) {
        const targets = this.getAvailableMediaTargets();
        if (targets.length < 2) return null;

        const activeMediaId = this.elements.viewer.querySelector('[data-media-id].is-active')?.dataset.mediaId;
        const currentIndex = Math.max(
          0,
          targets.findIndex((thumbnail) => thumbnail.dataset.target === activeMediaId)
        );
        const directionOffset = direction === 'previous' ? -1 : 1;
        const nextIndex = (currentIndex + directionOffset + targets.length) % targets.length;
        const target = targets[nextIndex];
        const targetMedia = this.elements.viewer.querySelector(`[data-media-id="${target.dataset.target}"]`);
        return { target, targetMedia };
      }

      prepareNavigationTarget(direction, fetchPriority = 'low') {
        const navigationTarget = this.getNavigationTarget(direction);
        if (!navigationTarget) return Promise.resolve(null);
        return this.prepareMediaImage(navigationTarget.targetMedia, fetchPriority);
      }

      navigateMedia(direction) {
        const navigationTarget = this.getNavigationTarget(direction);
        if (!navigationTarget) return;
        const { target, targetMedia } = navigationTarget;
        if (this.ltpMobileLoop) {
          const slider = this.elements.viewer.slider;
          const active = slider.querySelector('[data-ltp-gallery-position].is-active');
          const slides = [...slider.children].filter((slide) => !slide.hidden);
          const index = slides.indexOf(active);
          const next = slides[index + (direction === 'previous' ? -1 : 1)];
          if (next) slider.scrollTo({ left: this.getGalleryScrollTarget(next, slider), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
          return;
        }
        const targetImage = this.getDeferredImage(targetMedia);
        if (targetImage) {
          const navigationToken = (this.navigationToken || 0) + 1;
          this.navigationToken = navigationToken;
          const canonical = this.dataset.ltpCanonicalGallery === 'true';
          const present = () => {
            if (navigationToken !== this.navigationToken) return;
            if (canonical && !targetImage.naturalWidth) {
              delete targetImage.ltpReadyPromise;
              return;
            }
            this.setActiveMedia(target.dataset.target, false, canonical ? 0 : 240, canonical);
            this.scheduleCanonicalImages();
          };
          if (canonical && targetImage.ltpDecoded && targetImage.ltpReadySource === targetImage.currentSrc) {
            present();
          } else {
            this.prepareMediaImage(targetMedia, canonical ? 'high' : 'auto').then(present);
          }
          return;
        }

        this.setActiveMedia(target.dataset.target, false);
      }

      updateNavigationState() {
        if (!this.elements.navigationButtons.length) return;

        const targets = this.getAvailableMediaTargets();
        const activeMediaId = this.elements.viewer.querySelector('[data-media-id].is-active')?.dataset.mediaId;
        const currentIndex = targets.findIndex((thumbnail) => thumbnail.dataset.target === activeMediaId);
        const previousButton = Array.from(this.elements.navigationButtons).find(
          (button) => button.dataset.galleryDirection === 'previous'
        );
        const nextButton = Array.from(this.elements.navigationButtons).find(
          (button) => button.dataset.galleryDirection === 'next'
        );

        const navigationUnavailable = targets.length < 2 || currentIndex === -1;
        if (previousButton) previousButton.disabled = navigationUnavailable;
        if (nextButton) nextButton.disabled = navigationUnavailable;
      }

      setActiveMedia(mediaId, prepend, transitionDuration = 0, startImmediately = false) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        if (this.ltpMobileLoop) {
          this.rebaseMobileSlide(activeMedia);
          this.selectMobileSlide(activeMedia, 'navigation');
          return;
        }
        this.loadDeferredImage(activeMedia);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this.dispatchCanonicalPositionChange(activeMedia, 'navigation');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        const positionActiveMedia = () => {
          if (!this.mql.matches || this.elements.thumbnails) {
            if (transitionDuration > 0) {
              this.animateGalleryScroll(activeMedia, transitionDuration);
            } else {
              this.cancelGalleryAnimation();
              if (this.dataset.ltpCanonicalGallery === 'true') {
                const slider = activeMedia.parentElement;
                slider.scrollTo({ left: this.getGalleryScrollTarget(activeMedia, slider), behavior: 'instant' });
              } else {
                activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
              }
            }
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        };
        if (startImmediately) {
          positionActiveMedia();
        } else {
          window.setTimeout(positionActiveMedia);
        }
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.updateNavigationState();
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      dispatchCanonicalPositionChange(currentElement, source) {
        if (this.dataset.ltpCanonicalGallery !== 'true' || !currentElement) return;
        this.dispatchEvent(
          new CustomEvent('ltp:gallery-position-change', {
            bubbles: true,
            detail: { currentElement, source },
          })
        );
      }

      animateGalleryScroll(activeMedia, duration) {
        const slider = activeMedia.parentElement;
        const currentAnimation = this.galleryAnimation;
        let scrollStyles;
        if (currentAnimation) {
          cancelAnimationFrame(currentAnimation.frame);
          scrollStyles = currentAnimation.scrollStyles;
          this.galleryAnimation = null;
        } else {
          scrollStyles = {
            behavior: slider.style.getPropertyValue('scroll-behavior'),
            behaviorPriority: slider.style.getPropertyPriority('scroll-behavior'),
            snapType: slider.style.scrollSnapType,
          };
          slider.style.setProperty('scroll-behavior', 'auto', 'important');
          slider.style.scrollSnapType = 'none';
        }

        const startLeft = slider.scrollLeft;
        const targetLeft = this.getGalleryScrollTarget(activeMedia, slider);
        const distance = targetLeft - startLeft;

        const restore = () => {
          if (scrollStyles.behavior) {
            slider.style.setProperty('scroll-behavior', scrollStyles.behavior, scrollStyles.behaviorPriority);
          } else {
            slider.style.removeProperty('scroll-behavior');
          }
          slider.style.scrollSnapType = scrollStyles.snapType;
          if (this.galleryAnimation?.restore === restore) this.galleryAnimation = null;
        };

        if (Math.abs(distance) < 1 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          slider.scrollLeft = targetLeft;
          restore();
          return;
        }

        const startTime = performance.now();
        const step = (time) => {
          const progress = Math.min(Math.max((time - startTime) / duration, 0), 1);
          const easedProgress = 1 - Math.pow(1 - progress, 3);
          slider.scrollLeft = startLeft + distance * easedProgress;
          if (progress < 1) {
            this.galleryAnimation.frame = requestAnimationFrame(step);
            return;
          }
          slider.scrollLeft = targetLeft;
          restore();
        };

        this.galleryAnimation = {
          frame: requestAnimationFrame(step),
          restore,
          scrollStyles,
        };
      }

      getGalleryScrollTarget(activeMedia, slider) {
        if (this.mql.matches) return activeMedia.offsetLeft;

        const sliderStyles = window.getComputedStyle(slider);
        const mediaStyles = window.getComputedStyle(activeMedia);
        const scrollPadding =
          Number.parseFloat(sliderStyles.scrollPaddingInlineStart) ||
          Number.parseFloat(sliderStyles.scrollPaddingLeft) ||
          0;
        const scrollMargin =
          Number.parseFloat(mediaStyles.scrollMarginInlineStart) ||
          Number.parseFloat(mediaStyles.scrollMarginLeft) ||
          0;
        const snapStart = activeMedia.offsetLeft - scrollPadding - scrollMargin;
        return Math.min(Math.max(snapStart, 0), slider.scrollWidth - slider.clientWidth);
      }

      cancelGalleryAnimation() {
        if (!this.galleryAnimation) return;
        cancelAnimationFrame(this.galleryAnimation.frame);
        this.galleryAnimation.restore();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        const announce = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        if (image.complete && image.naturalWidth) {
          announce();
        } else {
          image.addEventListener('load', announce, { once: true });
        }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
